/**
 * The words an opposition case is described in, shared by the list, the
 * dossier and the activity feed (W10).
 *
 * Vue-free like the rest of `utils/` — it reads the catalogue through
 * `i18n.global.t`, the same way `dates.ts` does — so the timeline wording is
 * unit-testable and cannot drift between the two pages that print it.
 */
import i18n, { intlLocale } from '@/i18n'
import type { CaseDeadline, CaseStatus } from '@/entities/oppositionCase'
import type { CaseEvent } from '@/services/oppositions.service'
import type { DeadlineTone } from '@/entities/raw'
import { formatDate } from './format'

const t = (key: string, named?: Record<string, unknown>, plural?: number): string => {
  const g = i18n.global as unknown as {
    t: (k: string, n?: Record<string, unknown> | number, p?: number) => string
  }
  if (named && plural != null) return g.t(key, named, plural)
  if (named) return g.t(key, named)
  if (plural != null) return g.t(key, plural)
  return g.t(key)
}

/** A catalogue lookup that falls back to the raw value for a key we do not know. */
function known(key: string, fallback: string): string {
  const text = t(key)
  return text === key ? fallback : text
}

export function statusLabel(status: string | null | undefined): string {
  return status ? known(`opp.status.${status}`, status) : '—'
}

export function outcomeLabel(outcome: string | null | undefined): string {
  return outcome ? known(`opp.outcome.${outcome}`, outcome) : ''
}

export function priorityText(priority: string | null | undefined): string {
  return priority ? known(`opp.priority.${priority}`, priority) : '—'
}

export function riskText(risk: string | null | undefined): string {
  return risk ? known(`opp.risk.${risk}`, risk) : '—'
}

export function verdictText(verdict: string | null | undefined): string {
  return verdict ? known(`opp.verdict.${verdict}`, verdict) : ''
}

/** A registry code as a reader knows it. EUIPO and WIPO are their own names. */
export function registryName(code: string | null | undefined): string {
  if (!code || code === '—') return '—'
  return code.toUpperCase() === 'BG' ? t('opp.registryBg') : code
}

/** The two-letter bubble on a jurisdiction badge. */
export function registryBubble(code: string | null | undefined): string {
  const c = (code ?? '').toUpperCase()
  if (c === 'EUIPO') return 'EU'
  if (c === 'WIPO') return 'WO'
  if (c === 'BG') return 'BG'
  return ''
}

/** "8 days left" / "Due today" / "3 days overdue", from the server's own count. */
export function daysText(daysLeft: number | null | undefined): string {
  if (daysLeft == null) return ''
  if (daysLeft < 0) return t('time.daysOverdue', { n: Math.abs(daysLeft) }, Math.abs(daysLeft))
  if (daysLeft === 0) return t('time.dueToday')
  return t('time.daysLeft', { n: daysLeft }, daysLeft)
}

/**
 * How loud a deadline is. Overdue is the loudest thing on the page, not the
 * quietest — an open date that has passed is the one a lawyer must see first.
 * A done deadline is quiet whatever its date.
 */
export function deadlineTone(d: Pick<CaseDeadline, 'daysLeft' | 'doneAt'> | null): DeadlineTone {
  if (!d || d.doneAt || d.daysLeft == null) return ''
  if (d.daysLeft <= 14) return 'urgent'
  if (d.daysLeft <= 30) return 'warn'
  return ''
}

export function formatBytes(bytes: number | null | undefined): string {
  const n = bytes ?? 0
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

/** A date and time, for a timeline line. */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const time = d.toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' })
  return `${formatDate(iso)}, ${time}`
}

const ICONS: Record<string, string> = {
  created: 'fa-solid fa-folder-plus',
  status_changed: 'fa-solid fa-flag',
  note: 'fa-regular fa-note-sticky',
  document_added: 'fa-solid fa-arrow-up-from-bracket',
  document_removed: 'fa-regular fa-trash-can',
  assigned: 'fa-solid fa-user-plus',
  deadline_added: 'fa-regular fa-calendar-plus',
  deadline_done: 'fa-regular fa-calendar-check',
  deadline_removed: 'fa-regular fa-calendar-xmark',
  updated: 'fa-regular fa-pen-to-square',
}

export function eventIcon(kind: string): string {
  return ICONS[kind] ?? 'fa-regular fa-circle-dot'
}

/** The row tone the mockup's timeline classes use. */
export function eventTone(e: Pick<CaseEvent, 'kind'>): '' | 'gold' | 'high' | 'neutral' {
  if (e.kind === 'status_changed') return 'gold'
  if (e.kind === 'deadline_removed' || e.kind === 'document_removed') return 'neutral'
  return ''
}

/**
 * One timeline event as one plain-English line.
 *
 * Every kind the API writes has a sentence; anything newer falls back to "Case
 * updated" rather than printing a machine word. `lawyers` resolves an
 * `assigned` event's user id to a name where the page knows the roster — the
 * event itself stores only the id.
 */
export function eventLine(e: CaseEvent, lawyers: Record<string, string> = {}): string {
  const p = e.payload ?? {}
  const body = e.body ?? ''
  switch (e.kind) {
    case 'created':
      return p.match_id != null ? t('opp.event.createdFromMatch') : t('opp.event.created')
    case 'status_changed': {
      const from = statusLabel(p.from as CaseStatus)
      const to = statusLabel(p.to as CaseStatus)
      return p.outcome && (p.to === 'resolved' || p.to === 'closed')
        ? t('opp.event.statusOutcome', { from, to, outcome: outcomeLabel(p.outcome) })
        : t('opp.event.status', { from, to })
    }
    case 'note':
      return t('opp.event.note', {
        category: known(`opp.noteCategory.${e.category}`, e.category ?? ''),
      })
    case 'document_added':
      return t('opp.event.documentAdded', { name: body })
    case 'document_removed':
      return t('opp.event.documentRemoved', { name: body })
    case 'assigned': {
      if (p.user_id == null) return t('opp.event.unassigned')
      // The server stores the name with the id since 2026-09-28; the roster
      // lookup covers events written before that.
      const name = (p.name as string | undefined) || lawyers[String(p.user_id)]
      return name ? t('opp.event.assigned', { name }) : t('opp.event.assignedSomeone')
    }
    case 'deadline_added':
      return t('opp.event.deadlineAdded', {
        title: body,
        date: formatDate(p.due_date as string),
      })
    case 'deadline_done':
      return t('opp.event.deadlineDone', { title: body })
    case 'deadline_removed':
      return t('opp.event.deadlineRemoved', { title: body })
    case 'updated': {
      if (p.window_closed_on) {
        return t('opp.event.windowClosed', { date: formatDate(p.window_closed_on as string) })
      }
      if (p.deadline_id != null) {
        return p.reopened
          ? t('opp.event.deadlineReopened', { title: body })
          : t('opp.event.deadlineEdited', { title: body })
      }
      if (p.document_id != null) return t('opp.event.documentUpdated', { name: body })
      if (p.previous_priority && p.priority) {
        return t('opp.event.priority', {
          from: priorityText(p.previous_priority as string),
          to: priorityText(p.priority as string),
        })
      }
      return t('opp.event.edited')
    }
    default:
      return t('opp.event.other')
  }
}

/**
 * The text an event carries beyond its line: a note's words, a reason for
 * closing. File names and deadline titles are already in the line.
 */
export function eventDetail(e: CaseEvent): string {
  if (e.kind === 'note' || e.kind === 'status_changed') return e.body ?? ''
  return ''
}

/** The line and its detail as one string — one interpolation, so no template
 * whitespace can put a space before the colon. */
export function eventText(e: CaseEvent, lawyers: Record<string, string> = {}): string {
  const detail = eventDetail(e)
  const line = eventLine(e, lawyers)
  return detail ? `${line}: “${detail}”` : line
}
